import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { TrendingUp, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VisibilityScoreBreakdown = ({ articleId }) => {
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (articleId) {
      loadBreakdown();
    }
  }, [articleId]);

  const loadBreakdown = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/articles/${articleId}/visibility-breakdown`);
      if (!response.ok) {
        throw new Error('Failed to load visibility breakdown');
      }
      const data = await response.json();
      setBreakdown(data);
    } catch (err) {
      setError(err.message);
      console.error('Error loading visibility breakdown:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-2 border-purple-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
            <span className="ml-2 text-slate-600">Loading visibility metrics...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-2 border-red-200 bg-red-50">
        <CardContent className="pt-6">
          <div className="text-center py-4">
            <p className="text-red-600">Failed to load visibility metrics</p>
            <Button 
              onClick={loadBreakdown} 
              variant="outline" 
              size="sm"
              className="mt-2"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!breakdown) return null;

  const percentageComplete = (breakdown.total_visibility_score / breakdown.max_possible_score) * 100;

  return (
    <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 via-white to-pink-50">
      <CardHeader className="border-b border-purple-100">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-bold text-purple-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Visibility Score Breakdown
          </CardTitle>
          <div className="text-right">
            <div className="text-3xl font-bold text-purple-600">
              {breakdown.total_visibility_score.toFixed(1)}
            </div>
            <div className="text-sm text-slate-600">
              out of {breakdown.max_possible_score} points
            </div>
          </div>
        </div>
        <div className="mt-4">
          <Progress 
            value={percentageComplete} 
            className="h-3 bg-slate-200"
          />
          <div className="text-xs text-slate-500 mt-1 text-right">
            {percentageComplete.toFixed(1)}% complete
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-6">
        <div className="space-y-4">
          {breakdown.components.map((component, index) => {
            const componentPercentage = (component.points / component.max_points) * 100;
            const hasValue = component.value > 0 || component.points > 0;
            
            return (
              <div 
                key={index} 
                className={`p-4 rounded-lg border-2 transition-all ${
                  hasValue 
                    ? 'bg-white border-purple-200 shadow-sm' 
                    : 'bg-slate-50 border-slate-200'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{component.icon}</span>
                    <div>
                      <h4 className="font-semibold text-slate-900">
                        {component.name}
                      </h4>
                      <p className="text-xs text-slate-500">
                        {component.description}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-bold ${
                      hasValue ? 'text-purple-600' : 'text-slate-400'
                    }`}>
                      {component.points.toFixed(2)}
                    </div>
                    <div className="text-xs text-slate-500">
                      / {component.max_points} pts
                    </div>
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="mt-2">
                  <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                    <span>
                      {component.name === 'Recency Bonus' 
                        ? `${component.value} years old`
                        : `${component.value} ${component.name.toLowerCase()}`
                      }
                    </span>
                    <span>{componentPercentage.toFixed(0)}%</span>
                  </div>
                  <Progress 
                    value={componentPercentage} 
                    className={`h-2 ${
                      hasValue ? 'bg-purple-100' : 'bg-slate-200'
                    }`}
                  />
                </div>
                
                {/* Tips for improvement */}
                {!hasValue && component.name !== 'Recency Bonus' && (
                  <div className="mt-2 text-xs text-slate-500 italic">
                    💡 Tip: {getImprovementTip(component.name)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Footer Note */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800">
            <strong>Note:</strong> Visibility score reflects research reach and impact across multiple channels. 
            Higher scores indicate broader dissemination and engagement with the research community.
          </p>
        </div>
        
        {breakdown.calculated_at && (
          <div className="mt-4 text-xs text-slate-400 text-center">
            Last calculated: {new Date(breakdown.calculated_at).toLocaleDateString()}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Helper function for improvement tips
const getImprovementTip = (componentName) => {
  const tips = {
    'PDF Downloads': 'Upload a PDF version to enable downloads and tracking',
    'Citations': 'Ensure your article has a valid DOI for citation tracking',
    'Altmetric Score': 'Share your research on social media and engage with the community',
    'PlumX Metrics': 'Promote your work through institutional repositories and ResearchGate'
  };
  return tips[componentName] || 'Keep promoting your research!';
};

export default VisibilityScoreBreakdown;
